import {Fragment, MouseEvent, ReactElement, useState} from 'react'
import {Trans} from 'react-i18next'
import IconButton from '@material-ui/core/IconButton'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import {useHistory} from 'react-router-dom'
import moment from 'moment'

import messages from '../../../../../assets/messages'
import {TauroRulesModal} from '../../../../Common/TauroRulesModal'
import {
  formatTauroDrawdownPercent,
  getTauroFactorisationLeverage,
  TauroAccountFactorisation,
} from '../../../../../common/utils/accounts'
import {getFormattedAmount} from '../../../../../common/utils'
import {formatTauroReloadFigure} from '../../../../Payments/Deposits/utils'
import {useStyles} from './styles'

type TauroFactorisationDetailsProps = {
  factorisation: TauroAccountFactorisation
  /** The account the tile belongs to; the reload button opens its deposit page. */
  accountId?: number | string
}

/**
 * The 10x parameters on the account tile plus the info icon that opens the full rules
 * (PT-6334). Everything comes from the snapshot the account was opened with, so a client
 * always reads the terms their own account runs under, not the current campaign config.
 *
 * Once the account hits the equity floor the trading parameters no longer apply, so the tile
 * swaps them for the liquidation notice: when it happened, the floor it happened at and the
 * balance that stays on the account (PT-6822). The remaining balance is the client residual
 * the liquidation service stored; only states written before the amounts existed fall back
 * to `balanceAfter`, and amounts show on ended liquidations (closed/settled) only (PT-7153).
 *
 * PT-7878: when the backend sends the account `status`, it decides the tile. A liquidated account
 * can be reloaded with a new deposit, a closed one cannot, a pending one waits for the market.
 * The `Reload account` button shows only while the backend says `canReload`.
 */
function TauroFactorisationDetails(props: TauroFactorisationDetailsProps): ReactElement {
  const {factorisation, accountId} = props
  const classes = useStyles()
  const history = useHistory()
  const [rulesOpen, setRulesOpen] = useState(false)

  const status = factorisation.status ?? null
  // PT-7878: after a reload the account is active again, the earlier liquidation record is history.
  const liquidation = status === 'ACTIVE' ? null : factorisation.liquidation ?? null
  const currency = factorisation.currency ?? 'USD'

  // PT-7153: only an ended liquidation carries amounts the tile may print — never a pending
  // one and never a status the tile does not know.
  const isEnded =
    liquidation != null && (liquidation.status === 'closed' || liquidation.status === 'settled')
  const remainingBalance = isEnded
    ? liquidation?.clientResidual ?? liquidation?.balanceAfter ?? null
    : null

  const leverage = getTauroFactorisationLeverage(factorisation)
  const parameters =
    liquidation == null
      ? [
          factorisation.multiplier != null && (
            <Trans
              {...messages.tauroTileMultiplier}
              values={{multiplier: factorisation.multiplier}}
            />
          ),
          factorisation.drawdownTriggerPercent != null && (
            <Trans
              {...messages.tauroTileDrawdown}
              values={{drawdown: formatTauroDrawdownPercent(factorisation.drawdownTriggerPercent)}}
            />
          ),
          leverage != null && <Trans {...messages.tauroTileLeverage} values={{leverage}} />,
        ].filter(Boolean)
      : []

  // The tile itself opens the account on click, and the pop up renders inside it, so every
  // click that belongs to the rules has to stop before it reaches the card.
  const reloadAccount = () => {
    if (accountId != null) history?.push(`/transactions/${accountId}/deposit`)
  }

  const triggeredDate = (liquidation?.triggeredAt ?? liquidation?.closedAt)
    ? moment.utc(liquidation?.triggeredAt ?? liquidation?.closedAt).format('D MMM YYYY')
    : ''
  const statusNotice =
    status === 'LIQUIDATION_PENDING'
      ? {title: messages.tauroStatusPendingTitle, body: messages.tauroStatusPendingBody}
      : status === 'CLOSED'
      ? {
          title: messages.tauroStatusClosedTitle,
          // Closed by staff, by the equity floor with a payout, or settled with no liquidation.
          body:
            liquidation == null
              ? messages.tauroStatusClosedPlain
              : liquidation.manual
              ? messages.tauroStatusClosedByTeam
              : messages.tauroStatusClosedAuto,
        }
      : status === 'LIQUIDATED'
      ? {
          title: messages.tauroStatusLiquidatedTitle,
          body: liquidation?.manual
            ? messages.tauroStatusLiquidatedByTeam
            : messages.tauroStatusLiquidatedAuto,
        }
      : null

  const keepClickOnTheRules = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }

  return (
    <div className={classes.container} onClick={keepClickOnTheRules}>
      {statusNotice != null ? (
        <div data-testid="tauro-status-notice">
          <Typography variant="subtitle2" className={classes.statusTitle}>
            <Trans {...statusNotice.title} />
          </Typography>
          <Typography variant="caption" className={classes.liquidationNotice} component="p">
            <Trans
              {...statusNotice.body}
              values={{
                floor: formatTauroReloadFigure(liquidation?.floor ?? factorisation.equityFloor ?? 0),
                currency,
                date: triggeredDate,
                paidOut: formatTauroReloadFigure(liquidation?.paidOut ?? liquidation?.clientResidual ?? 0),
              }}
            />
          </Typography>
          {factorisation.canReload === true && status === 'LIQUIDATED' && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              className={classes.reloadButton}
              onClick={reloadAccount}
            >
              <Trans {...messages.tauroReloadButton} />
            </Button>
          )}
        </div>
      ) : liquidation != null ? (
        <Typography variant="caption" className={classes.liquidationNotice}>
          <Trans
            {...(liquidation.status === 'pending'
              ? messages.tauroLiquidationPendingNotice
              : remainingBalance != null
              ? messages.tauroLiquidationNotice
              : messages.tauroLiquidationNoticeNoAmount)}
            values={{
              floor: getFormattedAmount({currency, amount: liquidation.floor ?? 0}),
              // triggeredAt is a UTC ISO string — format it in UTC so the printed date
              // matches the server records regardless of the browser timezone (PT-7153).
              date: liquidation.triggeredAt
                ? moment.utc(liquidation.triggeredAt).format('D MMM YYYY')
                : '',
              amount:
                remainingBalance != null
                  ? getFormattedAmount({currency, amount: remainingBalance})
                  : '',
            }}
          />
        </Typography>
      ) : (
        parameters.map((parameter, index) => (
          <Fragment key={index}>
            {index > 0 && (
              <Typography variant="caption" className={classes.separator}>
                ·
              </Typography>
            )}
            <Typography variant="caption" className={classes.parameter}>
              {parameter}
            </Typography>
          </Fragment>
        ))
      )}
      <IconButton
        className={classes.infoButton}
        aria-label={messages.tauroTileRulesButton.defaults}
        onClick={() => setRulesOpen(true)}
      >
        <InfoOutlinedIcon className={classes.infoIcon} />
      </IconButton>
      <TauroRulesModal
        open={rulesOpen}
        onClose={() => setRulesOpen(false)}
        rules={factorisation}
      />
    </div>
  )
}

export {TauroFactorisationDetails}
